import { useLayoutEffect, useRef } from 'react';
import useEvent from './useEvent';

type Callbacks<T extends HTMLElement> = {
  onMount?: MountCallback<T>;
  onResize: ResizeCallback<T>;
};

type MountCallback<T extends HTMLElement> = (target: T) => void | undefined;

type Options = ResizeObserverOptions;

type ResizeCallback<T extends HTMLElement> = (
  target: T,
  entries: ResizeObserverEntry[],
  observer: ResizeObserver,
) => void | undefined;

export const useResizeObserverEffect = <T extends HTMLElement = HTMLDivElement>(
  getEl: () => T | null,
  callbacks: Callbacks<T>,
  options?: Options,
) => {
  const getTarget = useEvent(getEl);
  const handleMount = useEvent(callbacks.onMount);
  const handleResize = useEvent(callbacks.onResize);

  useLayoutEffect(() => {
    const el = getTarget();

    if (!el) return undefined;

    handleMount?.(el);

    const observer = new ResizeObserver((...args) => handleResize(el, ...args));

    observer.observe(el, {
      box: options?.box,
    });

    return () => observer.disconnect();
  }, [getTarget, handleMount, handleResize, options?.box]);
};

const useResizeObserver = <T extends HTMLElement = HTMLDivElement>(
  callbacks: Callbacks<T>,
  options?: Options,
) => {
  const targetRef = useRef<T>(null);

  useResizeObserverEffect(() => targetRef.current, callbacks, options);

  return targetRef;
};

export default useResizeObserver;
